import {
  createContext,
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AlertCircle, CheckCircle2, Info, LoaderCircle, X } from "lucide-react";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  intent?: "brand" | "neutral" | "success" | "warning" | "danger";
  emphasis?: "solid" | "outline" | "ghost";
  busy?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { intent = "brand", emphasis = "solid", busy = false, disabled, className = "", children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={`button button--${intent} button--${emphasis} ${className}`}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...props}
    >
      <span className="button__content">
        {children}
      </span>
      {busy && <LoaderCircle className="button__spinner" aria-hidden="true" />}
    </button>
  );
});

export function Brand({ compact = false, variant = "default" }: { compact?: boolean; variant?: "default" | "mobile-home" | "west-coast" }) {
  return (
    <a className={`brand ${variant === "west-coast" ? "brand--west-coast" : ""}`} href="/" aria-label={variant === "west-coast" ? "西海岸首页" : "星河娱乐首页"}>
      <span className="brand__mark" aria-hidden="true">
        <img src={variant === "west-coast" ? "/assets/west-coast/app-icon.png" : variant === "mobile-home" ? "/assets/xinghe-orbit-mark.svg" : "/assets/xinghe-brand-mark-v2.png"} alt="" />
      </span>
      {!compact && (
        <span className="brand__copy">
          <strong>{variant === "west-coast" ? "西海岸" : "星河娱乐"}</strong>
          <small>{variant === "west-coast" ? "WEST COAST" : "XINGHE ENTERTAINMENT"}</small>
        </span>
      )}
    </a>
  );
}

export function Spinner({ label = "正在加载" }: { label?: string }) {
  return (
    <div className="spinner" role="status">
      <LoaderCircle aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

type FieldBase = {
  label: string;
  error?: string;
  hint?: string;
  id?: string;
};

export const Field = forwardRef<HTMLInputElement, FieldBase & InputHTMLAttributes<HTMLInputElement>>(function Field(
  { label, error, hint, id: providedId, className = "", ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <label className="field" htmlFor={id}>
      <span className="field__label" id={`${id}-label`}>{label}</span>
      <input ref={ref} id={id} className={`input ${className}`} aria-labelledby={props["aria-label"] ? undefined : `${id}-label`} aria-invalid={Boolean(error)} aria-describedby={describedBy} {...props} />
      <span className={`field__message ${error ? "field__message--error" : ""}`} id={describedBy}>
        {error ?? hint ?? "\u00A0"}
      </span>
    </label>
  );
});

export function SelectField({ label, error, hint, id: providedId, children, ...props }: FieldBase & SelectHTMLAttributes<HTMLSelectElement>) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <label className="field" htmlFor={id}>
      <span className="field__label" id={`${id}-label`}>{label}</span>
      <select id={id} className="input select" aria-labelledby={props["aria-label"] ? undefined : `${id}-label`} aria-invalid={Boolean(error)} aria-describedby={describedBy} {...props}>
        {children}
      </select>
      <span className={`field__message ${error ? "field__message--error" : ""}`} id={describedBy}>
        {error ?? hint ?? "\u00A0"}
      </span>
    </label>
  );
}

export function TextAreaField({ label, error, hint, id: providedId, ...props }: FieldBase & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <label className="field" htmlFor={id}>
      <span className="field__label" id={`${id}-label`}>{label}</span>
      <textarea id={id} className="input textarea resize-none" aria-labelledby={props["aria-label"] ? undefined : `${id}-label`} aria-invalid={Boolean(error)} aria-describedby={describedBy} {...props} />
      <span className={`field__message ${error ? "field__message--error" : ""}`} id={describedBy}>
        {error ?? hint ?? "\u00A0"}
      </span>
    </label>
  );
}

export function Modal({
  open,
  title,
  description,
  children,
  onClose,
  closeLabel = "关闭弹窗",
  preventClose = false,
  variant = "default",
}: {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  closeLabel?: string;
  preventClose?: boolean;
  variant?: "default" | "game" | "auth";
}) {
  const titleId = useId();
  const descriptionId = useId();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const closeState = useRef({ onClose, preventClose });
  closeState.current = { onClose, preventClose };

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement;
    const root = document.getElementById("root");
    root?.setAttribute("inert", "");
    const previousOverflow = document.body.style.overflow;
    if (variant !== "default") document.body.style.overflow = "hidden";
    const timer = window.setTimeout(() => {
      const target = surfaceRef.current?.querySelector<HTMLElement>("[data-autofocus]")
        ?? surfaceRef.current?.querySelector<HTMLElement>("input, select, textarea, button, a[href]");
      target?.focus();
    }, 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !closeState.current.preventClose) closeState.current.onClose();
      if (event.key !== "Tab" || !surfaceRef.current) return;
      const focusable = Array.from(
        surfaceRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], iframe, [tabindex]:not([tabindex='-1'])"),
      ).filter((element) => !element.closest("[hidden], [inert]") && element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
      root?.removeAttribute("inert");
      if (variant !== "default") document.body.style.overflow = previousOverflow;
      previousFocus.current?.focus();
    };
  }, [open, variant]);

  if (!open) return null;
  return createPortal(
    <div className={`modal${variant !== "default" ? ` modal--${variant}` : ""}`} role="presentation">
      <button className="modal__backdrop" aria-label={closeLabel} onClick={preventClose ? undefined : onClose} />
      <div
        ref={surfaceRef}
        className="modal__surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <div className="modal__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <button className="icon-button" type="button" aria-label={closeLabel} onClick={onClose} disabled={preventClose}>
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="modal__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

type ToastTone = "success" | "info" | "error";
type ToastItem = { id: string; message: string; tone: ToastTone };
type ToastContextValue = { push: (message: string, tone?: ToastTone) => void };
const ToastContext = createContext<ToastContextValue | null>(null);
let toastSequence = 0;

function toastId() {
  return typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `toast-${Date.now()}-${++toastSequence}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const recent = useRef(new Map<string, number>());
  const push = useCallback((message: string, tone: ToastTone = "success") => {
    const now = Date.now();
    if (now - (recent.current.get(message) ?? 0) < 2000) return;
    recent.current.set(message, now);
    const id = toastId();
    setToasts((current) => [...current.slice(-3), { id, message, tone }]);
    if (tone !== "error") window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 4500);
  }, []);
  const value = useMemo(() => ({ push }), [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-label="通知">
        {toasts.map((toast) => {
          const Icon = toast.tone === "success" ? CheckCircle2 : toast.tone === "error" ? AlertCircle : Info;
          return (
            <div className={`toast toast--${toast.tone}`} key={toast.id} role={toast.tone === "error" ? "alert" : "status"}>
              <Icon aria-hidden="true" />
              <span>{toast.message}</span>
              <button aria-label="关闭通知" onClick={() => setToasts((current) => current.filter((item) => item.id !== toast.id))}>
                <X aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside ToastProvider");
  return context;
}

export function StatusBadge({ tone, children, ...props }: HTMLAttributes<HTMLSpanElement> & { tone: "success" | "warning" | "danger" | "info" | "neutral" }) {
  return (
    <span className={`status status--${tone}`} {...props}>
      <span className="status__dot" aria-hidden="true" />
      {children}
    </span>
  );
}

export function EmptyState({ title, message, action }: { title: string; message: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <span aria-hidden="true">✦</span>
      <h3>{title}</h3>
      <p>{message}</p>
      {action}
    </div>
  );
}
